import constants from '@playcastdotio/GlobalConfig';
import { generateQRCode } from '@playcastdotio/Qrcode';
import { useEffect, useState, useRef } from 'react';
import { useParams } from 'react-router-dom';
import pcLogo from '../../assets/logo.png';
import { connect, disconnect, getIsReady, sendMessage, setIsReady } from '../../api/modules/realtime';
import { InvitationMessageType, websocket, Invitation } from '@playcastdotio/Messaging';
import { invitationStore } from '../../api/stores/invitationStore';

const convertImageToBase64 = async (imageUrl: string): Promise<string> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        reject(new Error('Failed to get canvas context'));
        return;
      }
      ctx.drawImage(img, 0, 0);
      try {
        const dataUrl = canvas.toDataURL('image/png');
        resolve(dataUrl);
      } catch (error) {
        reject(error);
      }
    };
    img.onerror = () => reject(new Error('Failed to load image'));
    img.src = imageUrl;
  });
};

export const ObsQr = () => {
  // Get the hash parameter from the URL
  const { hash } = useParams<{ hash: string }>();
  const [invitation, setInvitation] = useState<Invitation | null>(null);
  const hasConnected = useRef(false);
  const queriedHashes = useRef<Set<string>>(new Set());

  // We want a blank webpage we can display our QR code on
  // Apply CSS overrides to make everything transparent for OBS

  useEffect(() => {
    // Apply global styles to ensure everything is transparent
    document.body.style.backgroundColor = 'transparent';
    document.documentElement.style.backgroundColor = 'transparent';

    // Additional global styles
    const styleElement = document.createElement('style');
    styleElement.textContent = `
      * {
        background-color: transparent !important;
      }
      body, html {
        background-color: transparent !important;
        margin: 0;
        padding: 0;
        overflow: hidden;
      }
    `;
    document.head.appendChild(styleElement);

    // Only connect if we haven't connected before
    if (!hasConnected.current) {
      connect(true);
      hasConnected.current = true;
    }

    const invitationListener = invitationStore.addListener((invitation) => {
      setInvitation(invitation);
    });

    // Clean up when component unmounts
    return () => {
      document.body.style.backgroundColor = '';
      document.documentElement.style.backgroundColor = '';
      if (styleElement.parentNode) {
        styleElement.parentNode.removeChild(styleElement);
      }
      disconnect();
      invitationStore.removeListener(invitationListener);
    };
  }, []);

  useEffect(() => {
    const checkReadiness = async () => {
      if (hash && !queriedHashes.current.has(hash)) {
        // Mark this hash as queried immediately to prevent duplicate queries
        queriedHashes.current.add(hash);

        if (getIsReady()) {
          sendMessage(
            websocket.generators.invitation(InvitationMessageType.INVITATION_MACHINE_HASH_QUERY, {
              machineIdHash: hash,
            }),
          );
        } else {
          // Make a promise that doesnt resolve until ready.
          await new Promise((resolve) => {
            const interval = setInterval(() => {
              if (getIsReady()) {
                clearInterval(interval);
                resolve(true);
              }
            }, 100);
          });

          sendMessage(
            websocket.generators.invitation(InvitationMessageType.INVITATION_MACHINE_HASH_QUERY, {
              machineIdHash: hash,
            }),
          );
        }
      }
    };

    checkReadiness();
  }, [hash]);

  return (
    <div
      className='h-full w-full bg-transparent'
      style={{
        backgroundColor: 'transparent',
        color: 'black', // Text color to make it visible against transparent background
      }}
    >
      <div className='flex h-full w-full flex-col items-center justify-center'>
        {invitation?.id && !invitation.invalid ? <QrCode inviteId={invitation.id} /> : <InvalidQrCode />}
      </div>
      {/* <div>{invitation?.id ?? 'No invitation found'}</div> */}
    </div>
  );
};

const QrCode = ({ inviteId }: { inviteId: string }): React.ReactNode => {
  const [qrCodeImage, setQrCodeImage] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const formattedUrl = (): string => {
      const baseUrl = 'player.playcast.io';
      const branchName = constants.VERSION.split('-')[1];
      if (!branchName) {
        return `https://${baseUrl}/j/${inviteId}`;
      }
      if (branchName === 'local') {
        return `http://localhost:4202/j/${inviteId}`;
      }
      return `https://${branchName}.${baseUrl}/j/${inviteId}`;
    };

    const generateCode = async () => {
      try {
        setLoading(true);
        const url = formattedUrl();
        const svgString = await generateQRCode(url, { logo: pcLogo });

        if (svgString) {
          setQrCodeImage(svgString);
        } else {
          throw new Error('No SVG string returned');
        }
      } catch (error) {
        console.error('Failed to generate QR code:', error);
        setQrCodeImage(null);
      } finally {
        setLoading(false);
      }
    };

    generateCode();
  }, [inviteId]); // Re-generate when hash changes

  if (!inviteId) {
    return <div>No invite ID provided</div>;
  }

  return loading ? (
    <div>Generating QR code...</div>
  ) : qrCodeImage ? (
    <div
      style={{
        width: '100%',
        height: '100%',
        position: 'fixed',
        top: 0,
        left: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <img
        src={qrCodeImage}
        alt={`QR Code for ${inviteId}`}
        style={{
          width: 'min(100%, 100vh)',
          height: 'min(100%, 100vh)',
          objectFit: 'contain',
        }}
      />
    </div>
  ) : (
    <div>Failed to generate QR code</div>
  );
};
const InvalidQrCode = () => {
  return <div></div>;
  // return <div>Invalid or no invitation</div>;
};
